/**
 * ═══════════════════════════════════════════════════════════════════
 * apiCache.ts — Stale-while-revalidate response cache for GETs
 * ═══════════════════════════════════════════════════════════════════
 *
 * Mirrors the mobile app's `api.ts` SWR cache (see
 * /hala-mobile-v23/src/services/api.ts lines 164-280-ish) so the web
 * UI feels as instant on revisit as the mobile UI.
 *
 * Three layers:
 *   L1 — in-memory Map        : sub-millisecond reads + in-flight dedup
 *   L2 — IndexedDB             : survives full page refresh, hydrated once on
 *                                app boot so the very first paint of every
 *                                page has real data
 *   L3 — network (axios)       : only fires if L1+L2 miss OR the cached
 *                                entry has crossed the freshness threshold
 *
 * TTLs:
 *   CACHE_TTL_MS                  = 60 s   — "fresh enough, skip network"
 *   CACHE_REVALIDATE_THRESHOLD_MS = 15 s   — "older than this → paint + refetch"
 *   CACHE_DISK_TTL_MS             = 7 days — "stale-but-paintable on cold start"
 *
 * Flow for a `swrGet(api, url, config)`:
 *   1. L1 hit AND age < TTL          → return cached synchronously
 *      └─ if age > revalidate-threshold, kick off background refetch
 *   2. L1 hit AND age < DISK_TTL     → return cached (stale-but-paintable)
 *                                       + background revalidate
 *   3. In-flight already             → return the same promise (dedup)
 *   4. Else                          → fire network, write L1+L2, return
 *
 * On mutation (POST/PUT/PATCH/DELETE): call `invalidateByPrefix(url)` so
 * subsequent GETs to the same base path see fresh data.
 *
 * On logout: call `clearPersistentApiCache()` so user-A's data never
 * leaks to user-B.
 *
 * Why native IndexedDB (no `idb` / `localforage`)?
 *   They're nice but add ~5 KB to the bundle. For ~10 cache reads/writes
 *   per session, native is fine — wrapped in small Promise helpers below.
 *
 * Why no SHA-1?
 *   The spec mentions sha1 as an option, but the deterministic
 *   `METHOD::url::sorted=params` string is already collision-safe inside
 *   a single domain (it's the *exact* URL after params normalisation).
 *   Hashing adds latency to a hot path for zero benefit. We use the raw
 *   string as the IndexedDB primary key.
 * ═══════════════════════════════════════════════════════════════════
 */

import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// ── TTLs ──
export const CACHE_TTL_MS = 60_000;                       // 60 s
export const CACHE_REVALIDATE_THRESHOLD_MS = 15_000;      // 15 s
export const CACHE_DISK_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// ── IndexedDB constants ──
const DB_NAME = 'hala_api_cache';
const DB_VERSION = 1;
const STORE = 'responses';
const INDEX_TS = 'byTs';

// ── L1 (RAM) + in-flight dedup ──
interface MemEntry { data: any; ts: number; }
const memCache = new Map<string, MemEntry>();
const inflight = new Map<string, Promise<AxiosResponse>>();

// ── Hydration state ──
// A single global promise so `hydrateApiCache()` is safe to call multiple
// times; subsequent calls await the same in-flight read.
let hydrationPromise: Promise<void> | null = null;

// ════════════════════════════════════════════════════════════════════
// Cache-key strategy
// ════════════════════════════════════════════════════════════════════
//
// Format:  <METHOD>::<url>::<sorted&joined=query=params>
//
// Sorting params alphabetically guarantees that
//   { warehouses: 'DSI', customers: 'BDP' }
// and
//   { customers: 'BDP', warehouses: 'DSI' }
// map to the same cache key — otherwise React's object-literal-rerender
// pattern would silently fragment the cache.
//
// Arrays and objects in params are JSON-stringified so `[1, 2]` does
// not collide with the string `"1,2"`. Undefined values are skipped
// (NOT serialized as the literal string "undefined" — that would
// poison the key when callers pass `{ x: state.x }` with x === undefined).
// ════════════════════════════════════════════════════════════════════
function buildCacheKey(method: string, url: string, params?: Record<string, any>): string {
  const m = method.toUpperCase();
  if (!params || typeof params !== 'object') return `${m}::${url}::`;
  const keys = Object.keys(params).filter(k => params[k] !== undefined);
  if (keys.length === 0) return `${m}::${url}::`;
  keys.sort();
  const joined = keys.map(k => {
    const v = params[k];
    if (v === null) return `${k}=`;
    if (typeof v === 'object') return `${k}=${JSON.stringify(v)}`;
    return `${k}=${String(v)}`;
  }).join('&');
  return `${m}::${url}::${joined}`;
}

// ════════════════════════════════════════════════════════════════════
// IndexedDB helpers — promisified, non-fatal on every error.
// ════════════════════════════════════════════════════════════════════
//
// Browser-feature detection: Safari Private mode, very old browsers,
// and certain Cypress/JSDOM test envs may not expose IndexedDB. In
// those cases we silently degrade to L1-only — no exceptions, no UI
// breakage; just no cross-reload persistence.
function hasIndexedDB(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
  } catch {
    return false;
  }
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDB(): Promise<IDBDatabase | null> {
  if (!hasIndexedDB()) return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    try {
      const req = window.indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'key' });
          store.createIndex(INDEX_TS, 'ts', { unique: false });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => {
        // Quota exhausted, blocked by browser policy, etc. Non-fatal.
        // eslint-disable-next-line no-console
        console.warn('[apiCache] IndexedDB open failed — degrading to L1-only', req.error);
        resolve(null);
      };
      req.onblocked = () => resolve(null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[apiCache] IndexedDB unavailable — degrading to L1-only', e);
      resolve(null);
    }
  });
  return dbPromise;
}

async function idbPut(key: string, entry: MemEntry): Promise<void> {
  const db = await openDB();
  if (!db) return;
  try {
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put({ key, data: entry.data, ts: entry.ts });
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();        // non-fatal
      tx.onabort = () => resolve();
    });
  } catch {
    /* non-fatal */
  }
}

async function idbDelete(key: string): Promise<void> {
  const db = await openDB();
  if (!db) return;
  try {
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    /* non-fatal */
  }
}

async function idbDeleteMany(keys: string[]): Promise<void> {
  if (keys.length === 0) return;
  const db = await openDB();
  if (!db) return;
  try {
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      keys.forEach(k => store.delete(k));
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    /* non-fatal */
  }
}

async function idbGetAll(): Promise<Array<{ key: string; data: any; ts: number }>> {
  const db = await openDB();
  if (!db) return [];
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve((req.result || []) as any);
      req.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

async function idbClear(): Promise<void> {
  const db = await openDB();
  if (!db) return;
  try {
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    });
  } catch {
    /* non-fatal */
  }
}

// ════════════════════════════════════════════════════════════════════
// Public API
// ════════════════════════════════════════════════════════════════════

/**
 * One-shot at app start. Reads every disk-cached response into L1 so
 * the first render of every page has data. Entries older than
 * CACHE_DISK_TTL_MS are dropped from disk as we go.
 *
 * Safe to await OR fire-and-forget — App.tsx fires-and-forgets so the
 * first paint is not blocked. If a page's GET fires before hydration
 * completes, the cache miss falls through to network (no harm done).
 */
export function hydrateApiCache(): Promise<void> {
  if (hydrationPromise) return hydrationPromise;
  hydrationPromise = (async () => {
    try {
      const all = await idbGetAll();
      const now = Date.now();
      const expired: string[] = [];
      let hydrated = 0;
      for (const row of all) {
        if (!row || typeof row.ts !== 'number') continue;
        if (now - row.ts > CACHE_DISK_TTL_MS) {
          expired.push(row.key);
          continue;
        }
        memCache.set(row.key, { data: row.data, ts: row.ts });
        hydrated++;
      }
      if (expired.length) void idbDeleteMany(expired);
      // eslint-disable-next-line no-console
      console.log(`[apiCache] hydrated ${hydrated} entries (${expired.length} expired) from IndexedDB`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[apiCache] hydrate failed:', e);
    }
  })();
  return hydrationPromise;
}

/**
 * Drop every cache entry whose URL starts with the given path.
 *
 * Called after a successful mutation so list/detail views refetch on
 * next access. The prefix is everything before the query-string, e.g.
 *
 *   invalidateByPrefix('/api/bookings')
 *     → drops cached keys for GET /api/bookings, /api/bookings?status=…,
 *       /api/bookings/42, /api/bookings/42/lines, etc.
 *
 * Matching rule: a key is dropped if its URL segment equals the prefix
 * OR begins with `prefix?` (same URL + query) OR begins with `prefix/`
 * (a sub-resource). This avoids the bug where `/api/book` would
 * accidentally invalidate `/api/bookings` if we used a naive
 * `startsWith`.
 */
export function invalidateByPrefix(prefix: string): void {
  if (!prefix) return;
  // Strip query string + trailing slash so caller can pass any URL form.
  let p = prefix.split('?')[0];
  while (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);

  const toDeleteIdb: string[] = [];
  for (const key of Array.from(memCache.keys())) {
    // key format: METHOD::url::sortedParams
    const parts = key.split('::');
    if (parts.length < 3) continue;
    const urlPart = parts[1];
    if (
      urlPart === p ||
      urlPart.startsWith(p + '?') ||
      urlPart.startsWith(p + '/') ||
      // Also handle when the cached URL had a query string baked in:
      // key includes the URL exactly as axios stored it.
      urlPart.split('?')[0] === p ||
      urlPart.split('?')[0].startsWith(p + '/')
    ) {
      memCache.delete(key);
      toDeleteIdb.push(key);
    }
  }
  if (toDeleteIdb.length) void idbDeleteMany(toDeleteIdb);
}

/**
 * Wipe EVERYTHING — call on logout so the next user doesn't see the
 * previous user's data. Clears L1, in-flight, AND IndexedDB.
 *
 * Returned promise resolves once the IDB clear completes (so callers
 * may optionally await it before redirecting to /login).
 */
export async function clearPersistentApiCache(): Promise<void> {
  memCache.clear();
  inflight.clear();
  hydrationPromise = null;
  await idbClear();
}

// ════════════════════════════════════════════════════════════════════
// swrGet — the actual stale-while-revalidate wrapper
// ════════════════════════════════════════════════════════════════════
//
// Signature matches axios's `get()` so the caller pattern stays
// identical:
//
//   const res = await swrGet(this.client, '/inventory', { params: {…} });
//   doStuff(res.data);
//
// The returned object is a real AxiosResponse on cache miss and a
// synthetic AxiosResponse-shaped object on cache hit (status: 200,
// statusText 'OK (cache)' or 'OK (stale-cache)'). Existing callers
// that only read `.data` keep working.
// ════════════════════════════════════════════════════════════════════

export interface SwrGetExtraConfig {
  /** Force a network fetch and ignore L1/L2. Result IS written to cache. */
  cacheBust?: boolean;
  /** Skip the cache entirely AND don't write — for downloads, signed URLs, etc. */
  _noCache?: boolean;
}

export async function swrGet<T = any>(
  api: AxiosInstance,
  url: string,
  config?: AxiosRequestConfig & SwrGetExtraConfig,
): Promise<AxiosResponse<T>> {
  const opts = config || {};

  // Hard bypass — used for binary downloads (responseType: 'blob') and
  // anything the caller explicitly opts out of.
  if (opts._noCache || opts.responseType === 'blob') {
    return rawGet<T>(api, url, opts);
  }

  const key = buildCacheKey('GET', url, opts.params);
  const now = Date.now();
  const cached = memCache.get(key);

  // ── 1. FRESH HIT ──
  // Inside the hot TTL: serve synchronously, optionally kick off a
  // soft revalidate if we're past the revalidate-threshold.
  if (!opts.cacheBust && cached && now - cached.ts < CACHE_TTL_MS) {
    if (now - cached.ts > CACHE_REVALIDATE_THRESHOLD_MS && !inflight.has(key)) {
      kickoffBackgroundRefetch(api, url, opts, key);
    }
    return synthesizeResponse<T>(cached.data, opts, 'OK (cache)');
  }

  // ── 2. STALE-BUT-PAINTABLE ──
  // Outside hot TTL but within disk TTL: paint the stale value AND
  // revalidate. This is the "never show loading on cold start" path.
  if (!opts.cacheBust && cached && now - cached.ts < CACHE_DISK_TTL_MS) {
    if (!inflight.has(key)) {
      kickoffBackgroundRefetch(api, url, opts, key);
    }
    return synthesizeResponse<T>(cached.data, opts, 'OK (stale-cache)');
  }

  // ── 3. IN-FLIGHT DEDUP ──
  // No usable cache. If a network request for this exact key is
  // already in-flight, await it instead of double-fetching.
  const existing = inflight.get(key);
  if (existing && !opts.cacheBust) {
    return existing as Promise<AxiosResponse<T>>;
  }

  // ── 4. NETWORK ──
  const promise = rawGet<T>(api, url, opts).then((res) => {
    // Don't cache non-2xx (defense in depth — axios usually rejects on
    // >=400 anyway, but custom validateStatus may resolve them).
    if (res && typeof res.status === 'number' && res.status < 400) {
      const entry: MemEntry = { data: res.data, ts: Date.now() };
      memCache.set(key, entry);
      void idbPut(key, entry);
    }
    inflight.delete(key);
    return res;
  }, (err) => {
    inflight.delete(key);
    throw err;
  });
  inflight.set(key, promise as Promise<AxiosResponse>);
  return promise;
}

// ── Helpers ──

function kickoffBackgroundRefetch(
  api: AxiosInstance,
  url: string,
  opts: AxiosRequestConfig,
  key: string,
): void {
  const p = rawGet(api, url, opts).then((res) => {
    if (res && typeof res.status === 'number' && res.status < 400) {
      const entry: MemEntry = { data: res.data, ts: Date.now() };
      memCache.set(key, entry);
      void idbPut(key, entry);
    }
    inflight.delete(key);
    return res;
  }, (err) => {
    inflight.delete(key);
    // Background error — swallow. The original cached value is still
    // sitting in L1 for the next reader. The next user-driven fetch
    // will surface the error properly.
    // eslint-disable-next-line no-console
    console.debug('[apiCache] background revalidate failed', { url, err: err?.message });
  });
  inflight.set(key, p as Promise<AxiosResponse>);
}

/** Get-through-axios that bypasses our wrapper. The `api.ts` module
 *  monkey-patches `client.get` to call `swrGet` — to avoid infinite
 *  recursion we must call the original raw method, which is stashed
 *  on the client at wrap time as `__rawGet`. Fallback to `api.request`
 *  if for any reason the stash is missing (defensive).
 */
function rawGet<T = any>(
  api: AxiosInstance,
  url: string,
  opts?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
  const raw = (api as any).__rawGet;
  if (typeof raw === 'function') {
    return raw(url, opts);
  }
  return api.request<T>({ ...(opts || {}), method: 'GET', url });
}

function synthesizeResponse<T>(
  data: T,
  opts: AxiosRequestConfig | undefined,
  statusText: string,
): AxiosResponse<T> {
  return {
    data,
    status: 200,
    statusText,
    headers: {},
    config: (opts || {}) as any,
    request: undefined,
  } as AxiosResponse<T>;
}
